import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { responseStatus, sentStatus, blockedReason, rollUp, summaryCells } from "./pilotStatus";
import InviteFitterCard from "./InviteFitterCard";
import NeedsSending from "./NeedsSending";

// THE PILOT IN ONE PLACE.
//
// Twenty-two people were invited to the first HG Education cohort: four going for real
// certification (Group A) and the rest testing (Group B). Until now the only way to know
// where any of them stood was to read four collections by hand. This is that read, done
// live, for an admin.
//
// Two halves, because a pilot generates two kinds of fact: WHO IS WHERE (the roster) and
// WHAT IS WRONG (the feedback). They are deliberately one screen away from each other — a
// defect only means something next to the person who hit it.

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function api(path) {
  const res = await fetch(`${API_URL}/api/education${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || `Request failed (${res.status})`);
  }
  return res.json();
}

const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { month: "short", day: "numeric",
                                    hour: "numeric", minute: "2-digit" });
};

// Colour carries the same meaning in both halves: green is good news, amber is waiting on
// someone, grey is nothing has happened yet, red is a problem.
const ACCESS_TONE = {
  "signed in": "good", enrolled: "good", "account created": "wait", "invite sent": "wait",
  "awaiting response": "wait", staged: "idle", declined: "bad",
};
const CATEGORY_TONE = { bug: "bad", wrong: "bad", confusing: "wait", suggestion: "idle" };

// Exported because these three are the whole of the tracker's judgement, and the repo tests
// judgement rather than markup. Everything else in this file is layout.
export const accessTone = (access) => ACCESS_TONE[access] || "idle";
export const categoryTone = (cat) => CATEGORY_TONE[cat] || "idle";

/** Where a note was filed, as a person would say it. screen_index is 0-based in the data
 *  and 1-based here: a tester counting screens starts at one. */
export function screenLocation(f = {}) {
  const bits = [];
  bits.push(f.chapter_number ? `Ch ${f.chapter_number}` : "—");
  if (f.lesson_number) bits.push(`L${f.lesson_number}`);
  if (Number.isInteger(f.screen_index)) bits.push(`screen ${f.screen_index + 1}`);
  const where = bits.join(" · ");
  return f.screen_title ? `${where} — ${f.screen_title}` : where;
}

/** The roster split into the two groups, each carrying the line that says whether it feeds
 *  the item statistics. Group B is excluded on purpose; saying so on the page stops anyone
 *  reading the pilot's numbers as if all 22 fed them. */
export function splitGroups(data) {
  const roster = (data && data.roster) || [];
  const summaries = (data && data.summary) || [];
  return ["A", "B"].map((g) => ({
    key: g,
    label: g === "A" ? "Group A — certification" : "Group B — testers",
    note: g === "A" ? "Counts toward item statistics" : "Excluded from item statistics",
    rows: roster.filter((r) => r.group === g),
    summary: summaries.find((s) => s.group === g),
  }));
}

/** A group's header counts, in the same plain words as the strip above it. `not sent` is
 *  listed first when it is non-zero because it is the only one of the five that is OUR work:
 *  the other four describe what the invited have or have not done. */
export function groupCounts(summary) {
  if (!summary) return [];
  const cells = [];
  if (summary.not_sent > 0)
    cells.push({ key: "not_sent", label: "not sent", value: summary.not_sent, tone: "bad" });
  cells.push({ key: "opted_in", label: "opted in", value: summary.opted_in, tone: "good" });
  cells.push({ key: "no_response", label: "no response yet",
               value: summary.no_response, tone: "wait" });
  cells.push({ key: "opted_out", label: "opted out", value: summary.opted_out, tone: "bad" });
  cells.push({ key: "started", label: "started", value: summary.started, tone: "idle" });
  cells.push({ key: "finished", label: "finished", value: summary.finished, tone: "idle" });
  return cells;
}

function Pill({ tone = "idle", children }) {
  return <span className={`pilot-pill pilot-pill-${tone}`}>{children}</span>;
}

function Bar({ percent }) {
  return (
    <div className="pilot-bar" title={`${percent}%`}>
      <div className="pilot-bar-fill"
           style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}

export default function PilotTracker({ user }) {
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [half, setHalf] = useState("roster");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const q = new URLSearchParams();
      if (category) q.set("category", category);
      if (status) q.set("status", status);
      const [t, f] = await Promise.all([
        api("/admin/tracker"),
        api(`/admin/feedback${q.toString() ? `?${q}` : ""}`),
      ]);
      setData(t);
      setFeedback(f);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [category, status]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => splitGroups(data), [data]);
  const roll = useMemo(() => rollUp(data), [data]);
  const viewerEmail = user?.email || "";

  if (loading && !data) {
    return (
      <div className="pilot-empty">
        <Loader2 size={18} className="pilot-spin" /> Loading the pilot&hellip;
      </div>
    );
  }
  if (error) return <div className="pilot-empty pilot-error">{error}</div>;

  return (
    <div className="pilot-tracker" data-testid="pilot-tracker">
      <div className="pilot-head">
        <div className="pilot-halves">
          <button className={`admin-tab ${half === "roster" ? "active" : ""}`}
                  onClick={() => setHalf("roster")} data-testid="pilot-tab-roster">
            Roster ({(data?.roster || []).length})
          </button>
          <button className={`admin-tab ${half === "feedback" ? "active" : ""}`}
                  onClick={() => setHalf("feedback")} data-testid="pilot-tab-feedback">
            Feedback ({feedback?.count ?? 0})
          </button>
        </div>
        <button className="pilot-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={13} /> {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* THE WHOLE PILOT, FIRST. The per-group headers below answer "how is Group A doing";
          this answers "where does the pilot stand", which is the question actually being
          asked when someone opens this tab. Summed from the same server summaries the group
          headers use, so the two readings cannot disagree.

          "Needs attention" is unsent invites only. A bounce is NOT in it and cannot be:
          edu_enrollment_queue records that an invite was sent and when, and has no delivery
          outcome — a hard bounce still reads as "no response yet" on the roster below. */}
      {half === "roster" && roll && (
        <div className="optin-strip optin-strip-lead" data-testid="pilot-summary-strip">
          {summaryCells(roll).map((c) => (
            <div key={c.key} className={`optin-cell optin-cell-${c.tone}`}
                 data-testid={`pilot-strip-${c.key}`}>
              <div className="optin-cell-value">{c.value}</div>
              <div className="optin-cell-label">{c.label}</div>
              {c.detail && <div className="optin-cell-detail">{c.detail}</div>}
            </div>
          ))}
        </div>
      )}

      {/* The one way to add a fitter without a developer. It refreshes this roster itself, so
          the new person appears under their group the moment they are minted. */}
      {half === "roster" && <InviteFitterCard onMinted={load} />}
      {half === "roster" && <NeedsSending onChanged={load} />}

      {half === "roster" && groups.map((g) => (
        <div key={g.key} className="pilot-group" data-testid={`pilot-group-${g.key}`}>
          <div className="pilot-group-head">
            <div>
              <div className="pilot-group-title">{g.label}</div>
              <div className="pilot-group-note">{g.note}</div>
            </div>
            {g.summary && (
              <div className="pilot-summary">
                <span className="pilot-summary-invited">
                  <b>{g.summary.invited}</b> of <b>{g.summary.people}</b> invited
                </span>
                {groupCounts(g.summary).map((c) => (
                  <span key={c.key} className={`pilot-count pilot-count-${c.tone}`}>
                    <b>{c.value}</b> {c.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <table className="pilot-table">
            <thead>
              <tr>
                <th>Name</th><th>Invite</th><th>Response</th><th>Access</th>
                <th>Progress</th><th>Quizzes</th><th>Hours</th><th>Final</th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map((r) => (
                <tr key={r.email || r.name}>
                  <td>
                    <div className="pilot-name">{r.name}</div>
                    <div className="pilot-email">{r.email}</div>
                    {r.invite_cc && <div className="pilot-email">cc {r.invite_cc}</div>}
                  </td>
                  <td data-testid={`pilot-sent-${r.email || r.name}`}>
                    {(() => {
                      const sent = sentStatus(r);
                      return sent.key === "system"
                        ? <span className="pilot-when">{when(sent.at)}</span>
                        : <Pill tone={sent.tone}>{sent.label}</Pill>;
                    })()}
                  </td>
                  {/* Plain words, not `in` / `—`. The status has to be readable without
                      first learning the table's shorthand, and an unsent row has to say
                      so rather than share a dash with genuine silence. */}
                  <td data-testid={`pilot-response-${r.email || r.name}`}>
                    {(() => {
                      const st = responseStatus(r, viewerEmail);
                      const why = st.key === "not_sent" ? blockedReason(r) : "";
                      return (
                        <>
                          <Pill tone={st.tone}>{st.label}</Pill>
                          {why && <div className="pilot-when">{why}</div>}
                        </>
                      );
                    })()}
                    {r.responded_at && <div className="pilot-when">{when(r.responded_at)}</div>}
                  </td>
                  <td><Pill tone={accessTone(r.access)}>{r.access}</Pill></td>
                  <td className="pilot-progress-cell">
                    <Bar percent={r.percent} />
                    <span className="pilot-when">
                      {r.lessons_complete}/{r.lessons_total} lessons
                    </span>
                  </td>
                  <td>{r.quizzes_passed}/{r.chapters_total}</td>
                  <td>{r.hours}</td>
                  <td>{r.final_exam_passed
                    ? <Pill tone="good">{r.certificate_number || "passed"}</Pill>
                    : <span className="pilot-when">&mdash;</span>}</td>
                </tr>
              ))}
              {!g.rows.length && (
                <tr><td colSpan={8} className="pilot-when">Nobody staged in this group.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {half === "feedback" && (
        <div className="pilot-group">
          <div className="pilot-filters">
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    data-testid="pilot-filter-category">
              <option value="">All categories</option>
              {(feedback?.categories || []).map((c) =>
                <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    data-testid="pilot-filter-status">
              <option value="">All states</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          {!(feedback?.feedback || []).length && (
            <div className="pilot-empty">Nothing filed yet.</div>
          )}
          {(feedback?.feedback || []).map((f) => (
            <div key={f.feedback_id} className="pilot-note">
              <div className="pilot-note-head">
                <Pill tone={categoryTone(f.category)}>{f.category}</Pill>
                {/* The exact screen, because "it was confusing" is not actionable and
                    "chapter 4, lesson 2, screen 3" is. */}
                <span className="pilot-where">{screenLocation(f)}</span>
                <span className="pilot-when">{f.user_email} &middot; {when(f.created_at)}</span>
              </div>
              <div className="pilot-note-body">{f.note}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
